/**
 * @file inward-po.ts
 * @module app/modules/inward/utils/inward-po
 * @description Pure helpers for showing purchase orders in the Inward module.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import {
  ShipmentAccepted,
  ShipmentPartialDelivered,
} from "../../../../utils/constants";

/** POs a GRN can be created against: Approved (2) and In Process (9). */
export const INWARD_ELIGIBLE_PO_STATUSES: readonly number[] = [
  ShipmentAccepted,
  ShipmentPartialDelivered,
];

export interface InwardPoQuantityType {
  type: number;
  stockCount?: number;
  receivedStockCount?: number;
  rejectedStockCount?: number;
}

/** Fields of a purchase-order list record that the Inward screens use. */
export interface InwardPurchaseOrder {
  _id: string;
  refKey: string;
  status: number;
  _createdAt?: string;
  business?: { name?: string; ownerName?: string; image?: string };
  warehouse?: { name?: string; address?: string };
  shipment?: { date?: string };
  variants?: { quantityTypes?: InwardPoQuantityType[] }[];
  deliveries?: unknown[];
  /** Loading bay manager + dock assigned for the next GRN (null when not assigned yet). */
  inwardAssignment?: {
    loadingBayManager?: { reference?: string; name?: string };
    dock?: { reference?: string; name?: string };
    gateEntryNumber?: string;
    /** Planned inward day, YYYY-MM-DD (East Africa Time) */
    scheduledDate?: string;
    assignedBy?: { name?: string };
    assignedAt?: string;
    currentSession?: { startedAt?: string; startedBy?: { name?: string } };
  } | null;
  /** What is pending next (computed by the API) */
  inwardNext?: InwardNextItem[];
}

export type InwardNextKind =
  | "assign"
  | "startInward"
  | "inwardInProgress"
  | "shortfallDecision"
  | "warehouseApproval"
  | "financeApproval"
  | "costAcknowledgement"
  | "complete";

export interface InwardNextItem {
  kind: InwardNextKind;
  label: string;
  grnRefKey?: string;
}

/** Units (VariantQuantityTypes): full name and short code. */
export const UNIT_LABELS: Readonly<
  Record<number, { short: string; full: string }>
> = {
  1: { short: "CWD", full: "Carton with Dozens" },
  2: { short: "DWP", full: "Dozens with Pieces" },
  3: { short: "P", full: "Pieces" },
  4: { short: "CWP", full: "Carton with Pieces" },
};

/** "Carton with Pieces (CWP)" */
export const unitLabel = (type: number): string => {
  const u = UNIT_LABELS[Number(type)];
  return u ? `${u.full} (${u.short})` : String(type);
};

/** Today in East Africa Time (UTC+3) as YYYY-MM-DD. */
export const eatToday = (now: Date = new Date()): string =>
  new Date(now.getTime() + 3 * 60 * 60 * 1000).toISOString().slice(0, 10);

/** Ordered minus already received minus rejected, never below zero. */
export const getPendingCount = (qt: InwardPoQuantityType): number =>
  Math.max(
    0,
    (Number(qt.stockCount) || 0) -
      (Number(qt.receivedStockCount) || 0) -
      (Number(qt.rejectedStockCount) || 0)
  );

/** Pending quantity per quantity type, summed across all variants of the PO. */
export const getPendingQuantities = (
  po: InwardPurchaseOrder
): { type: number; count: number }[] => {
  const totals = new Map<number, number>();
  (po.variants ?? []).forEach((variant) =>
    (variant.quantityTypes ?? []).forEach((qt) => {
      const type = Number(qt.type);
      totals.set(type, (totals.get(type) ?? 0) + getPendingCount(qt));
    })
  );
  return Array.from(totals.entries())
    .filter(([, count]) => count > 0)
    .map(([type, count]) => ({ type, count }));
};

export const formatQuantities = (
  quantities: { type: number; count: number }[]
): string =>
  quantities.map(({ type, count }) => `${count} ${unitLabel(type)}`).join(", ");

/** A GRN can be created when the PO is Approved / In Process and something is still pending. */
export const canCreateGrnForPo = (po: InwardPurchaseOrder): boolean =>
  INWARD_ELIGIBLE_PO_STATUSES.includes(po.status) &&
  getPendingQuantities(po).length > 0;

export const getInwardPoStatusLabel = (status: number): string => {
  if (status === ShipmentAccepted) return "Approved";
  if (status === ShipmentPartialDelivered) return "In Process";
  return "-";
};
